import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/api/client"
import { formatKwh } from "@/lib/format"
import { fehlerNachricht, useToast } from "@/lib/toast"
import { useDocumentTitle } from "@/lib/useDocumentTitle"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import type { SeuBericht, SeuDefinition } from "@/api/types"

function heuteMinus(tage: number): string {
  const d = new Date()
  d.setDate(d.getDate() - tage)
  return d.toISOString().slice(0, 10)
}

function BerichtZeile({ seu }: { seu: SeuDefinition }) {
  const [von, setVon] = useState(heuteMinus(30))
  const [bis, setBis] = useState(new Date().toISOString().slice(0, 10))

  const berichtQuery = useQuery({
    queryKey: ["seu-bericht", seu.id, von, bis],
    queryFn: async () =>
      (await api.get<SeuBericht>(`/significant-energy-use/${seu.id}/bericht`, { params: { von, bis } })).data,
  })

  return (
    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
      <input
        type="date"
        value={von}
        onChange={(e) => setVon(e.target.value)}
        className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
      />
      <span className="text-slate-400">bis</span>
      <input
        type="date"
        value={bis}
        onChange={(e) => setBis(e.target.value)}
        className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
      />
      {berichtQuery.isLoading && (
        <span className="h-5 w-40 animate-pulse rounded-md bg-slate-100" />
      )}
      {berichtQuery.isError && (
        <span className="flex items-center gap-2 rounded-md bg-red-50 px-2 py-0.5 text-red-700">
          Bericht konnte nicht geladen werden.
          <button
            type="button"
            onClick={() => berichtQuery.refetch()}
            className="rounded-md border border-red-300 px-1.5 py-0.5 text-xs font-medium hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </span>
      )}
      {berichtQuery.data && (
        <span
          className={
            berichtQuery.data.schwellwertUeberschritten
              ? "inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
              : "inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
          }
        >
          Summe: {formatKwh(berichtQuery.data.verbrauchssumme)}
          {berichtQuery.data.schwellwertKwh &&
            ` / Schwellwert: ${formatKwh(berichtQuery.data.schwellwertKwh)}`}
          {berichtQuery.data.schwellwertUeberschritten && " — ÜBERSCHRITTEN"}
        </span>
      )}
    </div>
  )
}

function SeuDefinitionSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [benennung, setBenennung] = useState("")
  const [bereich, setBereich] = useState("")
  const [prozessschritt, setProzessschritt] = useState("")
  const [schwellwertKwh, setSchwellwertKwh] = useState("")
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setBenennung("")
    setBereich("")
    setProzessschritt("")
    setSchwellwertKwh("")
    setFeldFehler({})
  }, [open])

  const anlegen = useMutation({
    mutationFn: async () =>
      (
        await api.post("/significant-energy-use", {
          benennung,
          bereich: bereich || undefined,
          prozessschritt: prozessschritt ? Number(prozessschritt) : undefined,
          schwellwertKwh: schwellwertKwh || undefined,
        })
      ).data,
    onSuccess: () => {
      setFeldFehler({})
      queryClient.invalidateQueries({ queryKey: ["seu"] })
      toast.success("SEU-Einsatzstelle angelegt.")
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const nachricht = fehlerNachricht(err, "Konnte nicht angelegt werden.")
      setFeldFehler({ form: nachricht })
      toast.error(nachricht, { retry: () => anlegen.mutate() })
    },
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    const neueFehler: Record<string, string> = {}
    if (!benennung.trim()) neueFehler.benennung = "Pflichtfeld"
    if (!prozessschritt) neueFehler.prozessschritt = "Pflichtfeld (ISO 50001)"
    setFeldFehler(neueFehler)
    if (Object.keys(neueFehler).length > 0) {
      document.getElementById(neueFehler.benennung ? "seu-benennung" : "seu-prozessschritt")?.focus()
      return
    }
    anlegen.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="energie.seu-definition" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Neue SEU-Einsatzstelle</SheetTitle>
          <SheetDescription>
            Verbrauchssumme je Einsatzstelle + Schwellwert-Auswertung (F4)
          </SheetDescription>
        </SheetHeader>
        <form className="grid gap-4 py-4" onSubmit={absenden} noValidate>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="seu-benennung">
              Benennung
            </label>
            <input
              id="seu-benennung"
              type="text"
              value={benennung}
              onChange={(e) => setBenennung(e.target.value)}
              aria-invalid={!!feldFehler.benennung}
              aria-describedby={feldFehler.benennung ? "seu-benennung-fehler" : undefined}
              className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.benennung ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
            />
            {feldFehler.benennung && (
              <p id="seu-benennung-fehler" className="mt-0.5 text-xs text-red-600">
                {feldFehler.benennung}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="seu-bereich">
              Bereich
            </label>
            <input
              id="seu-bereich"
              type="text"
              value={bereich}
              onChange={(e) => setBereich(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="seu-prozessschritt">
              Prozessschritt
            </label>
            <input
              id="seu-prozessschritt"
              type="number"
              value={prozessschritt}
              onChange={(e) => setProzessschritt(e.target.value)}
              aria-invalid={!!feldFehler.prozessschritt}
              aria-describedby={feldFehler.prozessschritt ? "seu-prozessschritt-fehler" : undefined}
              className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.prozessschritt ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
            />
            {feldFehler.prozessschritt && (
              <p id="seu-prozessschritt-fehler" className="mt-0.5 text-xs text-red-600">
                {feldFehler.prozessschritt}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="seu-schwellwert">
              Schwellwert (kWh, optional)
            </label>
            <input
              id="seu-schwellwert"
              type="number"
              step="0.001"
              value={schwellwertKwh}
              onChange={(e) => setSchwellwertKwh(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          {feldFehler.form && <p className="text-sm text-red-600">{feldFehler.form}</p>}
          <SheetFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              {anlegen.isPending ? "Speichert…" : "Speichern"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export function SeuPage() {
  useDocumentTitle("SEU / ISO 50001")
  const [sheetOffen, setSheetOffen] = useState(false)

  const seuQuery = useQuery({
    queryKey: ["seu"],
    queryFn: async () => (await api.get<SeuDefinition[]>("/significant-energy-use")).data,
  })

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Significant Energy Use / ISO 50001
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Verbrauchssumme je Einsatzstelle + Schwellwert-Auswertung (F4)
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSheetOffen(true)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          SEU-Einsatzstelle anlegen
        </button>
      </div>

      <SeuDefinitionSheet open={sheetOffen} onOpenChange={setSheetOffen} />

      {seuQuery.isLoading && (
        <div className="mt-6 space-y-4">
          <div className="h-16 animate-pulse rounded-lg border border-slate-200 bg-white" />
          <div className="h-16 animate-pulse rounded-lg border border-slate-200 bg-white" />
        </div>
      )}

      {seuQuery.isError && (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Einsatzstellen konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => seuQuery.refetch()}
            className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {seuQuery.data?.map((seu) => (
          <div key={seu.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="font-medium text-slate-900">
              {seu.benennung}{" "}
              {seu.bereich && <span className="text-slate-400">({seu.bereich})</span>}
            </div>
            <div className="text-xs text-slate-500">Prozessschritt {seu.prozessschritt}</div>
            <BerichtZeile seu={seu} />
          </div>
        ))}
        {!seuQuery.isLoading && seuQuery.data?.length === 0 && (
          <p className="text-slate-400">
            Noch keine Einsatzstellen definiert — mit „SEU-Einsatzstelle anlegen" die erste
            Einsatzstelle anlegen.
          </p>
        )}
      </div>
    </div>
  )
}
